// Iter 134d — Nudge A/B Accuracy dashboard.
//
// Admin-only page that compares the hit-rate of predictions whose
// confidence was nudged up vs down vs unchanged vs missing (pre-134),
// so we can quantify whether the Iter 134 context nudges actually
// lift accuracy.
//
// Endpoint: GET /api/admin/nudge-ab/stats?days=30
//
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, RefreshCcw,
  ChartBar, Sparkles, Target, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import TopNav from "../components/TopNav";
import { toast } from "sonner";

const WINDOWS = [7, 14, 30, 60, 90, 180];

const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}%`);
const fmtNum = (v) => (v == null ? "—" : Number(v).toLocaleString());

const BucketTone = {
  up:        { ring: "ring-emerald-500/40", text: "text-emerald-600 dark:text-emerald-400", Icon: TrendingUp, label: "Nudged ↑ (confidence raised)" },
  down:      { ring: "ring-rose-500/40",    text: "text-rose-600 dark:text-rose-400",       Icon: TrendingDown, label: "Nudged ↓ (confidence lowered)" },
  unchanged: { ring: "ring-slate-400/40",   text: "text-foreground",                        Icon: Minus,        label: "Unchanged (no nudge fired)" },
  missing:   { ring: "ring-amber-500/30",   text: "text-amber-700 dark:text-amber-400",     Icon: AlertTriangle, label: "Missing (no nudge metadata — pre-Iter134)" },
};

const BucketCard = ({ bucketKey, data }) => {
  const tone = BucketTone[bucketKey];
  const Icon = tone.Icon;
  return (
    <div
      className={`rounded-2xl border border-border bg-card p-5 ring-1 ${tone.ring}`}
      data-testid={`nudge-bucket-${bucketKey}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-muted flex items-center justify-center">
            <Icon className={`w-4.5 h-4.5 ${tone.text}`} />
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
              Bucket
            </p>
            <p className="text-sm font-bold">{tone.label}</p>
          </div>
        </div>
        {bucketKey !== "missing" && data.avg_nudge_pp !== 0 && (
          <Badge variant="outline" className="font-tabular">
            avg {data.avg_nudge_pp > 0 ? "+" : ""}{data.avg_nudge_pp}pp
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Samples</p>
          <p className={`mt-0.5 font-display font-bold text-2xl font-tabular ${tone.text}`}>
            {fmtNum(data.total)}
          </p>
          <p className="text-xs text-muted-foreground">{data.hits} hits</p>
        </div>
        <div>
          <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Hit rate</p>
          <p className={`mt-0.5 font-display font-bold text-2xl font-tabular ${tone.text}`}>
            {fmtPct(data.hit_rate)}
          </p>
          <p className="text-xs text-muted-foreground">
            ≥65% conf: {fmtPct(data.high_conf_hit_rate)}
          </p>
        </div>
      </div>

      {Object.keys(data.by_market || {}).length > 0 && (
        <div className="border-t border-border pt-3">
          <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-2">
            By market
          </p>
          <div className="space-y-1">
            {Object.entries(data.by_market)
              .sort((a, b) => b[1].total - a[1].total)
              .slice(0, 8)
              .map(([mt, m]) => (
                <div key={mt} className="flex items-center justify-between text-xs">
                  <span className="text-foreground/80 truncate pr-2">{mt}</span>
                  <span className="flex items-center gap-2 shrink-0 font-tabular">
                    <span className="text-muted-foreground">{m.hits}/{m.total}</span>
                    <span className={`font-bold ${
                      m.hit_rate >= 65 ? "text-emerald-600 dark:text-emerald-400"
                        : m.hit_rate >= 50 ? "text-amber-600 dark:text-amber-400"
                        : "text-rose-600 dark:text-rose-400"
                    }`}>
                      {m.hit_rate}%
                    </span>
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

const HeadlineLiftCard = ({ overall }) => {
  const { nudged, baseline_hit_rate, lift_pp, status, note } = overall || {};
  const isPositive = (lift_pp ?? 0) > 0;
  const tone = status === "warming_up"
    ? "border-amber-500/40 bg-amber-500/5"
    : isPositive
    ? "border-emerald-500/50 bg-emerald-500/5"
    : "border-rose-500/40 bg-rose-500/5";
  const liftIcon = status === "warming_up"
    ? <Sparkles className="w-5 h-5 text-amber-500" />
    : isPositive
    ? <ArrowUpRight className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
    : <ArrowDownRight className="w-5 h-5 text-rose-600 dark:text-rose-400" />;

  return (
    <div
      className={`rounded-2xl border ${tone} p-6 mb-6`}
      data-testid="nudge-headline-lift"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="h-11 w-11 rounded-2xl bg-background flex items-center justify-center shadow-sm">
          {liftIcon}
        </div>
        <div>
          <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-muted-foreground">
            Iter 134 Nudge Lift
          </p>
          <h2 className="font-display font-bold text-2xl tracking-tight">
            {status === "warming_up"
              ? "Warming up — collecting settled samples"
              : isPositive
              ? `+${lift_pp}pp lift vs baseline`
              : `${lift_pp}pp vs baseline`}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <p className="eyebrow text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
            Nudged predictions
          </p>
          <p className="mt-1 font-display font-bold text-3xl font-tabular">
            {nudged?.total ? fmtNum(nudged.total) : "0"}
          </p>
          <p className="text-xs text-muted-foreground">
            {nudged?.hits ?? 0} hits · {fmtPct(nudged?.hit_rate)}
          </p>
        </div>
        <div>
          <p className="eyebrow text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
            Baseline hit-rate
          </p>
          <p className="mt-1 font-display font-bold text-3xl font-tabular">
            {fmtPct(baseline_hit_rate)}
          </p>
          <p className="text-xs text-muted-foreground">
            From unchanged / pre-134 settled picks
          </p>
        </div>
        <div>
          <p className="eyebrow text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
            Status
          </p>
          <p className={`mt-1 font-display font-bold text-3xl font-tabular ${
            status === "warming_up" ? "text-amber-600 dark:text-amber-400"
              : isPositive ? "text-emerald-600 dark:text-emerald-400"
              : "text-rose-600 dark:text-rose-400"
          }`}>
            {status === "warming_up" ? "Pending" : isPositive ? "Lifting" : "Drag"}
          </p>
          <p className="text-xs text-muted-foreground">
            Min 30 nudged settled
          </p>
        </div>
      </div>
      {note && (
        <p className="mt-4 text-xs text-muted-foreground italic">
          {note}
        </p>
      )}
    </div>
  );
};

const AdminNudgeABPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (windowDays) => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/nudge-ab/stats`,
        { params: { days: windowDays }, timeout: 15000 }
      );
      setStats(data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      } else {
        toast.error("Failed to load nudge stats");
      }
      setStats(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth");
      return;
    }
    load(days);
  }, [authLoading, user, days, load, navigate]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-nudge-ab-page">
      <TopNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-muted-foreground">
              Admin · Iter 134 Lab
            </p>
            <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-1">
              Nudge A/B Accuracy
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Compares hit-rate of context-nudged predictions vs the unchanged
              baseline, broken down by market.  Refresh after the daily 23:00 UTC
              settlement cron to see new data land.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(days)}
            disabled={refreshing}
            data-testid="nudge-refresh-btn"
          >
            <RefreshCcw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Window selector */}
        <div className="flex items-center gap-1 mb-6">
          <ChartBar className="w-4 h-4 text-muted-foreground mr-2" />
          <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mr-3">
            Window
          </span>
          {WINDOWS.map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-xs font-bold tabular-nums px-3 py-1.5 rounded-full border transition-colors ${
                days === d
                  ? "bg-foreground text-background border-foreground"
                  : "bg-card text-foreground border-border hover:bg-muted"
              }`}
              data-testid={`nudge-window-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>

        {loading ? (
          <div className="rounded-2xl border border-border bg-card p-12 text-center text-muted-foreground">
            Loading nudge stats…
          </div>
        ) : !stats ? (
          <div className="rounded-2xl border border-border bg-card p-12 text-center text-muted-foreground">
            No data available.
          </div>
        ) : (
          <>
            <HeadlineLiftCard overall={stats.overall} />

            <div className="mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-[11px] font-bold tracking-[0.22em] uppercase text-foreground">
                Bucket breakdown
              </h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
              {["up", "down", "unchanged", "missing"].map((b) => (
                <BucketCard key={b} bucketKey={b} data={stats.by_bucket[b] || {}} />
              ))}
            </div>

            <p className="mt-6 text-xs text-muted-foreground">
              Window since {new Date(stats.since).toLocaleString()} · {stats.window_days} day window
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminNudgeABPage;
